import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Chip, Button, Alert,
  CircularProgress, LinearProgress, Collapse, IconButton, Divider,
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown, ExpandMore, ExpandLess, Info,
  PlayArrow,
} from '@mui/icons-material';
import axios from 'axios';

const REFRESH_INTERVAL = 60;
const IST_FMT = {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
};
function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+') && !s.includes('-', 10)) s += 'Z';
  return new Date(s).toLocaleString('en-IN', IST_FMT);
}
function fmtNum(v, dec = 2) {
  if (v == null) return '-';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

const COLUMNS = [
  { id: 'strategy', label: 'Strategy', align: 'left', sortable: true },
  { id: 'symbol', label: 'Symbol', align: 'left', sortable: true },
  { id: 'futures_price', label: 'Future Price', align: 'right', sortable: true },
  { id: 'spot_price', label: 'Cash Price', align: 'right', sortable: true },
  { id: 'premium_basis', label: 'Basis', align: 'right', sortable: true },
  { id: 'annual_coc', label: 'Annual COC', align: 'right', sortable: true },
  { id: 'lot_size', label: 'Lot', align: 'right', sortable: true },
  { id: 'contract_value', label: 'Contract Val', align: 'right', sortable: true },
  { id: 'investment_required', label: 'Margin ~20%', align: 'right', sortable: true },
  { id: 'days_to_expiry', label: 'Days', align: 'right', sortable: true },
  { id: 'spot_source', label: 'Src', align: 'center', sortable: false },
  { id: 'detected_at', label: 'Detected (IST)', align: 'left', sortable: true },
  { id: 'action', label: 'Trade', align: 'center', sortable: false },
];

export default function ArbitrageOpportunities({ apiUrl }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [sortBy, setSortBy] = useState('annual_coc');
  const [sortDir, setSortDir] = useState('desc');
  const [infoOpen, setInfoOpen] = useState(false);

  // Trade dialog state
  const [tradeOpen, setTradeOpen] = useState(false);
  const [tradeOpp, setTradeOpp] = useState(null);
  const [tradeMode, setTradeMode] = useState('paper');
  const [executing, setExecuting] = useState(false);

  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/arbitrage/opportunities`, { headers });
      setOpportunities(res.data);
      setLastUpdate(new Date());
      setError('');
    } catch (err) {
      if (err.response?.status !== 404) setError('Failed to fetch');
      setOpportunities([]);
    } finally { setLoading(false); setCountdown(REFRESH_INTERVAL); }
  }, [apiUrl]);

  useEffect(() => {
    fetchOpportunities();
    intervalRef.current = setInterval(fetchOpportunities, REFRESH_INTERVAL * 1000);
    countdownRef.current = setInterval(() => {
      setCountdown((p) => (p <= 1 ? REFRESH_INTERVAL : p - 1));
    }, 1000);
    return () => { clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [fetchOpportunities]);

  const handleRefresh = () => {
    fetchOpportunities();
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchOpportunities, REFRESH_INTERVAL * 1000);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    const copy = [...opportunities];
    copy.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === 'detected_at') { va = new Date(va || 0).getTime(); vb = new Date(vb || 0).getTime(); }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? (va ?? 0) - (vb ?? 0) : (vb ?? 0) - (va ?? 0);
    });
    return copy;
  }, [opportunities, sortBy, sortDir]);

  const openTradeDialog = (opp) => {
    setTradeOpp(opp); setTradeMode('paper'); setTradeOpen(true); setSuccess('');
  };

  const executeTrade = async () => {
    if (!tradeOpp) return;
    setExecuting(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${apiUrl}/arbitrage/execute-trade`, {
        symbol: tradeOpp.symbol,
        spot_price: tradeOpp.spot_price,
        futures_price: tradeOpp.futures_price,
        trade_mode: tradeMode,
        lot_size: tradeOpp.lot_size || 1,
      }, { headers });
      if (res.data.success) {
        const trades = res.data.trades;
        const summary = trades.map(t =>
          `${t.mode.toUpperCase()}: ${t.spot_leg.action} ${t.spot_leg.qty} spot @ ${t.spot_leg.price} + ${t.futures_leg.action} 1 fut @ ${t.futures_leg.price}`
        ).join(' | ');
        setSuccess(`${res.data.strategy} trade executed for ${res.data.symbol}! ${summary}`);
        setTradeOpen(false);
        fetchOpportunities();
      } else {
        setError('Trade execution failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Trade failed');
    } finally { setExecuting(false); }
  };

  const clearExecuted = async (symbol) => {
    try {
      await axios.post(`${apiUrl}/arbitrage/clear-executed/${symbol}`, {}, { headers });
      setSuccess(`Cleared executed flag for ${symbol}`);
      fetchOpportunities();
    } catch (err) {
      setError('Failed to clear executed flag');
    }
  };

  const progressPct = ((REFRESH_INTERVAL - countdown) / REFRESH_INTERVAL) * 100;
  const ccCount = opportunities.filter(o => (o.premium_basis || o.difference || 0) > 0).length;
  const rcCount = opportunities.filter(o => (o.premium_basis || o.difference || 0) <= 0).length;
  const avgCOC = opportunities.length > 0
    ? (opportunities.reduce((s, o) => s + (o.annual_coc || 0), 0) / opportunities.length).toFixed(2) : '0';

  return (
    <Box data-testid="arbitrage-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Arbitrage Opportunities</Typography>
          {lastUpdate && <Typography variant="caption" color="text.secondary">Updated: {toIST(lastUpdate.toISOString())} IST</Typography>}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip label={`${countdown}s`} size="small" variant="outlined" data-testid="refresh-countdown" />
          <Button variant="outlined" size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
            onClick={handleRefresh} disabled={loading} data-testid="refresh-arbitrage-btn">Refresh</Button>
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={progressPct} sx={{ mb: 2, height: 2, borderRadius: 2 }} />

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Collapsible Info Box */}
      <Paper sx={{ mb: 2 }} variant="outlined">
        <Box display="flex" alignItems="center" justifyContent="space-between"
          sx={{ px: 2, py: 1, cursor: 'pointer' }} onClick={() => setInfoOpen(!infoOpen)} data-testid="info-box-toggle">
          <Box display="flex" alignItems="center" gap={1}>
            <Info color="info" fontSize="small" />
            <Typography variant="subtitle2">Arbitrage Strategies & Logic (Click to {infoOpen ? 'collapse' : 'expand'})</Typography>
          </Box>
          <IconButton size="small">{infoOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        <Collapse in={infoOpen}>
          <Divider />
          <Box sx={{ px: 2, py: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'success.main', borderRadius: 2, mb: 2 }}>
                  <Chip label="Cash-and-Carry" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>When:</strong> Futures price &gt; Spot price (positive basis)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Action:</strong> BUY asset in cash/spot market + SELL (short) futures contract
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Profit:</strong> Lock in the premium basis. At expiry, futures & spot converge — you pocket the difference minus carrying costs.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 2, mb: 2 }}>
                  <Chip label="Reverse Cash-and-Carry" color="error" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>When:</strong> Futures price &lt; Spot price (negative basis / backwardation)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Action:</strong> SHORT-SELL asset in cash market + BUY (long) futures contract
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Profit:</strong> Benefit when futures price rises to meet cash price, or cover short at lower price.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="primary" gutterBottom>Trade Execution</Typography>
            <Typography variant="body2" color="text.secondary">
              Click <strong>Trade</strong> on any row. Choose <strong>Paper</strong>, <strong>Real</strong>, or <strong>Both</strong>.
              The system places 2 legs: <strong>Lot Size x 1</strong> in spot/cash + <strong>1 lot</strong> in futures.
              Annual COC = (Basis / Cash Price) x (365 / Days to Expiry) x 100.
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      {/* Summary */}
      {opportunities.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h6">{opportunities.length}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Cash-and-Carry</Typography>
              <Typography variant="h6" color="success.main">{ccCount}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Reverse Cash-and-Carry</Typography>
              <Typography variant="h6" color="error.main">{rcCount}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Avg Annual COC</Typography>
              <Typography variant="h6">{avgCOC}%</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {sorted.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>No arbitrage opportunities found. System scans every minute.</Alert>
      )}

      {/* Table */}
      {sorted.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} align={col.align}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.73rem' }}>
                    {col.sortable ? (
                      <TableSortLabel active={sortBy === col.id}
                        direction={sortBy === col.id ? sortDir : 'asc'}
                        onClick={() => handleSort(col.id)} data-testid={`sort-${col.id}`}>
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((opp, idx) => {
                const basis = opp.premium_basis ?? opp.difference ?? 0;
                const isCashCarry = basis > 0;
                return (
                  <TableRow key={opp.id || idx} data-testid={`arb-row-${idx}`}
                    sx={{ '&:hover': { bgcolor: 'action.hover' }, opacity: opp.executed ? 0.5 : 1 }}>
                    <TableCell>
                      <Chip
                        label={isCashCarry ? 'Cash & Carry' : 'Reverse C&C'}
                        size="small"
                        color={isCashCarry ? 'success' : 'error'}
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {isCashCarry
                          ? <TrendingUp sx={{ mr: 0.5, color: 'success.main', fontSize: 16 }} />
                          : <TrendingDown sx={{ mr: 0.5, color: 'error.main', fontSize: 16 }} />}
                        <Typography variant="body2" fontWeight="bold">{opp.symbol}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmtNum(opp.futures_price)}</TableCell>
                    <TableCell align="right">{fmtNum(opp.spot_price)}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color={isCashCarry ? 'success.main' : 'error.main'}>
                        {fmtNum(basis)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`${(opp.annual_coc || 0).toFixed(1)}%`} size="small"
                        color={(opp.annual_coc || 0) >= 0 ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell align="right">{opp.lot_size || 1}</TableCell>
                    <TableCell align="right">{fmtNum(opp.contract_value, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(opp.investment_required, 0)}</TableCell>
                    <TableCell align="right">{opp.days_to_expiry || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip label={(opp.spot_source || 'nse').toUpperCase()} size="small"
                        variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 18 }} />
                    </TableCell>
                    <TableCell><Typography variant="caption" noWrap>{toIST(opp.detected_at)}</Typography></TableCell>
                    <TableCell align="center">
                      {!opp.executed ? (
                        <Button size="small" variant="contained" color={isCashCarry ? 'success' : 'error'}
                          startIcon={<PlayArrow />}
                          onClick={() => openTradeDialog(opp)}
                          sx={{ fontSize: '0.7rem', py: 0.3, minWidth: 0 }}
                          data-testid={`trade-btn-${opp.symbol}`}>
                          Trade
                        </Button>
                      ) : (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip label="Executed" size="small" color="default" sx={{ fontSize: '0.65rem' }} />
                          <Button size="small" variant="outlined" color="warning"
                            onClick={() => clearExecuted(opp.symbol)}
                            sx={{ fontSize: '0.6rem', py: 0, px: 0.5, minWidth: 0, lineHeight: 1.4 }}
                            data-testid={`clear-btn-${opp.symbol}`}>
                            Clear
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Trade Dialog */}
      <Dialog open={tradeOpen} onClose={() => setTradeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Execute Arbitrage Trade — {tradeOpp?.symbol}
        </DialogTitle>
        <DialogContent>
          {tradeOpp && (
            <Box>
              <Chip
                label={tradeOpp.premium_basis > 0 ? 'Cash-and-Carry' : 'Reverse Cash-and-Carry'}
                color={tradeOpp.premium_basis > 0 ? 'success' : 'error'}
                sx={{ mb: 2 }}
              />

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Spot/Cash Price</Typography>
                    <Typography variant="h6">{fmtNum(tradeOpp.spot_price)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Futures Price</Typography>
                    <Typography variant="h6">{fmtNum(tradeOpp.futures_price)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Basis</Typography>
                    <Typography fontWeight="bold" color={(tradeOpp.premium_basis || 0) > 0 ? 'success.main' : 'error.main'}>
                      {fmtNum(tradeOpp.premium_basis)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Lot Size</Typography>
                    <Typography fontWeight="bold">{tradeOpp.lot_size}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Annual COC</Typography>
                    <Typography fontWeight="bold">{(tradeOpp.annual_coc || 0).toFixed(2)}%</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>Trade Legs:</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                {tradeOpp.premium_basis > 0 ? (
                  <>
                    <Typography variant="body2"><strong>Leg 1 (Spot):</strong> BUY {tradeOpp.lot_size} shares of {tradeOpp.symbol} on NSE @ {fmtNum(tradeOpp.spot_price)}</Typography>
                    <Typography variant="body2"><strong>Leg 2 (Futures):</strong> SELL 1 lot of {tradeOpp.symbol} on NFO @ {fmtNum(tradeOpp.futures_price)}</Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2"><strong>Leg 1 (Spot):</strong> SHORT {tradeOpp.lot_size} shares of {tradeOpp.symbol} on NSE @ {fmtNum(tradeOpp.spot_price)}</Typography>
                    <Typography variant="body2"><strong>Leg 2 (Futures):</strong> BUY 1 lot of {tradeOpp.symbol} on NFO @ {fmtNum(tradeOpp.futures_price)}</Typography>
                  </>
                )}
              </Paper>

              <Typography variant="subtitle2" gutterBottom>Select Trade Mode:</Typography>
              <ToggleButtonGroup
                value={tradeMode} exclusive
                onChange={(_, v) => v && setTradeMode(v)}
                fullWidth sx={{ mb: 1 }}
              >
                <ToggleButton value="paper" data-testid="trade-mode-paper"
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Paper (Virtual)
                </ToggleButton>
                <ToggleButton value="real" data-testid="trade-mode-real"
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Real (Live)
                </ToggleButton>
                <ToggleButton value="both" data-testid="trade-mode-both"
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Both
                </ToggleButton>
              </ToggleButtonGroup>
              {tradeMode === 'real' && (
                <Alert severity="warning" sx={{ mt: 1 }}>Real trades will be sent to your broker. Ensure broker is connected.</Alert>
              )}
              {tradeMode === 'both' && (
                <Alert severity="info" sx={{ mt: 1 }}>Both paper and real trades will be placed simultaneously.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTradeOpen(false)}>Cancel</Button>
          <Button variant="contained"
            color={tradeOpp?.premium_basis > 0 ? 'success' : 'error'}
            onClick={executeTrade} disabled={executing}
            startIcon={executing ? <CircularProgress size={16} /> : <PlayArrow />}
            data-testid="confirm-trade-btn">
            {executing ? 'Executing...' : 'Execute Trade'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
